import { useCallback, useMemo, useState } from 'react';
import { styled } from '@mui/material';
import { Grid } from '@mui/system';
import { useSelector } from 'react-redux';
import { type CustomPrimaryUser } from 'api';
import { ResponsibleNewUserData } from 'dashboard409a/pages/Information/InformationRequestModal/CreateNewUserDialog/types';
import { RootStateType } from 'dashboard409a/states/store';
import addCompanyUserToCompanyDashboard from 'dashboard409a/utilities/userManagement';
import UserCard from 'pages/UserPermissions/components/UserCard';
import UserDirectory from 'pages/UserPermissions/components/UserDirectory';
import { prettyLastLogin, prettyUsername } from 'pages/UserPermissions/utils/prettyUserData';
import { useGetUserDetails } from 'services/hooks/useUserSelected';

const PREFIX = 'UserManagement';

const classes = {
  section: `${PREFIX}-section`,
  borderLeft: `${PREFIX}-borderLeft`,
};

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`& .${classes.section}`]: {
    height: 'calc(100vh - 15rem)',
    padding: '1rem',
  },

  [`& .${classes.borderLeft}`]: {
    borderLeft: `3px solid ${theme.palette.grey[200]}`,
  },
}));

const UserManagement = () => {
  const [currentUser, setCurrentUser] = useState<CustomPrimaryUser>();
  const { data: userSelected } = useGetUserDetails(currentUser?.id ?? 0);

  const { currentCompany } = useSelector((state: RootStateType) => state.global);
  const handleAddUser = useCallback(
    async (userData: ResponsibleNewUserData, _: any, handleDialogClose: () => void) => {
      if (currentCompany?.id) {
        await addCompanyUserToCompanyDashboard({ userData, currentCompanyId: currentCompany?.id, handleDialogClose });
      }
    },
    [currentCompany]
  );

  const userFullNameOrEmail = useMemo(() => prettyUsername(userSelected), [userSelected]);

  const lastLogin = useMemo(() => prettyLastLogin(userSelected), [userSelected]);

  return (
    <StyledGrid container style={{ margin: '-1rem' }}>
      <Grid size={{ sm: 6 }} className={classes.section}>
        {userSelected && (
          <UserCard
            name={userFullNameOrEmail}
            lastLogin={lastLogin}
            email={userSelected?.email ?? ''}
            isPrimaryUser={currentUser?.is_primary_contact}
            pictureUrl={userSelected?.profile?.picture_url ?? ''}
            disabled={!userSelected?.id}
          />
        )}
      </Grid>
      <Grid size={{ sm: 6 }} className={`${classes.section} ${classes.borderLeft}`}>
        <UserDirectory
          setCurrentUser={setCurrentUser}
          isFirmAdmin
          isSuperUser
          handleAddUser={handleAddUser}
          companyList={[]}
          fundList={[]}
          selectedCompany={currentCompany}
          is409a
        />
      </Grid>
    </StyledGrid>
  );
};

export default UserManagement;
